import { snakeCase, truncate } from 'lodash';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { FormCheck, Overlay, Popover, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  ArrowDownRight,
  ArrowReturnLeft,
  ArrowUpRight,
  ArrowsAngleContract,
  ArrowsAngleExpand,
  ArrowsExpand,
  CircleFill
} from 'react-bootstrap-icons';
import { assetCategoriesOptions, monthSmall, primaryBankAccountName } from '../../../../../helpers/constants';
import { getTranslatedMonth } from '../../../../../helpers/localization';
import useLocalization from '../../../../../hooks/useLocalization';
import LTRNumberCell from '../../../../common/LTRNumberCell';
import TextWithEdit from '../../../../common/TextWithEdit';
import {
  addMonthAndYear,
  findYearlyRateOfCompouding,
  formatCurrency,
  formatPercentage,
  getFinalYearOfStrategy,
  getStrategyYears,
  getTransactionsAtMonthYear,
  getValueAtMonthYear,
  isPrimaryBankAccount,
  isRSUAsset,
  sortInstruments
} from './helper';
import Summary from '../../../../calculators/IrrBreakdownSummary';
import AlertModal from '../../../../common/AlertModal';
import { Button as ButtonTW } from '../../../../ui/button';
import { Copy, PlusCircleIcon, Trash2, PenIcon, Check, X } from 'lucide-react';

const hasMonthlyValues = cashflowItem => {
  return cashflowItem.yearlyValues?.some(y => y.monthlyValues?.some(m => m !== 0));
};

const checkIfExpanded = (expandedYears, year) => expandedYears.includes(year);

const getKey = (...uniqueTerms) => uniqueTerms.join('-');

const getAnnualAppreciation = strategy => {
  const startYear = strategy.initialYear;
  const startMonth = strategy.initialMonth || 0;

  const endYear = getFinalYearOfStrategy(strategy);

  const assetsStartTotal = strategy['assets']
    .filter(
      a =>
        isPrimaryBankAccount(a) ||
        (!a.isInvalid && !a.toBeMerged && !a.editMode && a.buyingMonth === startMonth && a.buyingYear === startYear)
    )
    .reduce((prevValue, a) => prevValue + a.value, 0);

  const liabilityStartTotal = strategy['liabilities']
    .filter(
      a => !a.isInvalid && !a.toBeMerged && !a.editMode && a.startMonth === startMonth && a.startYear === startYear
    )
    .reduce((prevValue, a) => prevValue + a.value, 0);

  const assetsEndTotal = strategy['assets']
    .filter(a => !a.isInvalid && !a.toBeMerged)
    .reduce((prevValue, a) => prevValue + getValueAtMonthYear(a, 11, endYear), 0);

  const liabilityEndTotal = strategy['liabilities']
    .filter(a => !a.isInvalid && !a.toBeMerged)
    .reduce((prevValue, a) => prevValue + getValueAtMonthYear(a, 11, endYear), 0);

  const valueAtStart = assetsStartTotal - liabilityStartTotal;
  const valueAtEnd = assetsEndTotal - liabilityEndTotal;

  if (valueAtEnd - valueAtStart === 0) return 0;

  return findYearlyRateOfCompouding(valueAtEnd, valueAtStart, strategy.totalYears ?? 10);
};

const transformBalanceSheetToCSV = (strategy, expandedMonthYears, terminalMonthYear, translate) => {
  // Create columns for react-csv-downloader
  const columns = [{ id: 'item', displayName: translate('balance_sheet') || 'Balance Sheet' }];
  expandedMonthYears.forEach(({ month, year }) => {
    columns.push({
      id: `${month}-${year}`,
      displayName: `${getTranslatedMonth(translate, month, { short: true })} ${year}`
    });
  });
  columns.push({ id: 'terminal', displayName: translate('terminal_value') || 'Terminal Value' });
  columns.push({ id: 'appreciation', displayName: translate('appreciation') || 'Appreciation' });

  const csvData = [];

  // Helper function to create empty row
  const createEmptyRow = () => {
    const row = { item: '' };
    expandedMonthYears.forEach(({ month, year }) => {
      row[`${month}-${year}`] = '';
    });
    row.terminal = '';
    row.appreciation = '';
    return row;
  };

  // Helper function to add a row
  const addRow = (name, values, appreciation = '') => {
    const row = { item: name };
    expandedMonthYears.forEach(({ month, year }) => {
      const value = values.find(v => v.month === month && v.year === year);
      row[`${month}-${year}`] = value ? Math.round(value.value) : 0;
    });
    const terminalValue = values.find(v => v.month === terminalMonthYear?.month && v.year === terminalMonthYear?.year);
    row.terminal = terminalValue ? Math.round(terminalValue.value) : 0;
    row.appreciation = appreciation;
    csvData.push(row);
  };

  // Assets Section
  const assetsHeaderRow = { item: translate('assets') || 'Assets' };
  expandedMonthYears.forEach(({ month, year }) => {
    assetsHeaderRow[`${month}-${year}`] = '';
  });
  assetsHeaderRow.terminal = '';
  assetsHeaderRow.appreciation = '';
  csvData.push(assetsHeaderRow);

  const assetsToShow = strategy['assets'].filter(a => !a.toBeMerged || a.isInvalid === false);

  // Group assets by category
  assetCategoriesOptions.forEach(category => {
    const assetsOfCategory = assetsToShow.filter(a =>
      a.categoryToShowIn ? a.categoryToShowIn === category : a.category === category
    );

    if (assetsOfCategory.length > 0) {
      const categoryHeaderRow = { item: translate(snakeCase(category)) || category };
      expandedMonthYears.forEach(({ month, year }) => {
        categoryHeaderRow[`${month}-${year}`] = '';
      });
      categoryHeaderRow.terminal = '';
      categoryHeaderRow.appreciation = '';
      csvData.push(categoryHeaderRow);

      assetsOfCategory.forEach(asset => {
        const values = expandedMonthYears.map(({ month, year }) => ({
          month,
          year,
          value: getValueAtMonthYear(asset, month, year)
        }));
        values.push({
          month: terminalMonthYear?.month,
          year: terminalMonthYear?.year,
          value: getValueAtMonthYear(asset, terminalMonthYear?.month, terminalMonthYear?.year)
        });

        const name = isPrimaryBankAccount(asset)
          ? translate('primary_bank_account')
          : asset.name +
            (asset.category === 'RSU' && asset.isVestedRSU
              ? ' (Vested)'
              : asset.category === 'RSU'
              ? ' (Unvested)'
              : '');
        const appreciation = (asset.returnAppreciation || asset.interest || 0)?.toFixed(2) + '%';
        addRow(name, values, appreciation);
      });

      // Category total
      const categoryTotalValues = expandedMonthYears.map(({ month, year }) => ({
        month,
        year,
        value: assetsOfCategory
          .filter(a => !a.isInvalid && !a.toBeMerged)
          .reduce((sum, a) => sum + getValueAtMonthYear(a, month, year), 0)
      }));
      categoryTotalValues.push({
        month: terminalMonthYear?.month,
        year: terminalMonthYear?.year,
        value: assetsOfCategory
          .filter(a => !a.isInvalid && !a.toBeMerged)
          .reduce((sum, a) => sum + getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year), 0)
      });
      addRow(`${translate('total') || 'Total'} - ${translate(snakeCase(category)) || category}`, categoryTotalValues);
      csvData.push(createEmptyRow()); // Empty row for spacing
    }
  });

  // Total Assets
  const totalAssetsValues = expandedMonthYears.map(({ month, year }) => ({
    month,
    year,
    value: assetsToShow
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce((sum, a) => sum + getValueAtMonthYear(a, month, year), 0)
  }));
  totalAssetsValues.push({
    month: terminalMonthYear?.month,
    year: terminalMonthYear?.year,
    value: assetsToShow
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce((sum, a) => sum + getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year), 0)
  });
  addRow(`${translate('total_assets') || 'Total Assets'}`, totalAssetsValues);
  csvData.push(createEmptyRow()); // Empty row for spacing

  // Liabilities Section
  const liabilitiesHeaderRow = { item: translate('liabilities') || 'Liabilities' };
  expandedMonthYears.forEach(({ month, year }) => {
    liabilitiesHeaderRow[`${month}-${year}`] = '';
  });
  liabilitiesHeaderRow.terminal = '';
  liabilitiesHeaderRow.appreciation = '';
  csvData.push(liabilitiesHeaderRow);

  const liabilitiesToShow = strategy['liabilities'].filter(l => !l.toBeMerged || l.isInvalid === false);

  liabilitiesToShow.forEach(liability => {
    const values = expandedMonthYears.map(({ month, year }) => ({
      month,
      year,
      value: getValueAtMonthYear(liability, month, year)
    }));
    values.push({
      month: terminalMonthYear?.month,
      year: terminalMonthYear?.year,
      value: getValueAtMonthYear(liability, terminalMonthYear?.month, terminalMonthYear?.year)
    });

    const appreciation = (liability.interest || 0)?.toFixed(2) + '%';
    addRow(liability.name, values, appreciation);
  });

  // Total Liabilities
  const totalLiabilitiesValues = expandedMonthYears.map(({ month, year }) => ({
    month,
    year,
    value: liabilitiesToShow
      .filter(l => !l.isInvalid && !l.toBeMerged)
      .reduce((sum, l) => sum + getValueAtMonthYear(l, month, year), 0)
  }));
  totalLiabilitiesValues.push({
    month: terminalMonthYear?.month,
    year: terminalMonthYear?.year,
    value: liabilitiesToShow
      .filter(l => !l.isInvalid && !l.toBeMerged)
      .reduce((sum, l) => sum + getValueAtMonthYear(l, terminalMonthYear?.month, terminalMonthYear?.year), 0)
  });
  addRow(`${translate('total_liabilities') || 'Total Liabilities'}`, totalLiabilitiesValues);
  csvData.push(createEmptyRow()); // Empty row for spacing

  // Equity Section
  const equityValues = expandedMonthYears.map(({ month, year }) => {
    const assetsTotal = assetsToShow
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce((sum, a) => sum + getValueAtMonthYear(a, month, year), 0);
    const liabilityTotal = liabilitiesToShow
      .filter(l => !l.isInvalid && !l.toBeMerged)
      .reduce((sum, l) => sum + getValueAtMonthYear(l, month, year), 0);
    return {
      month,
      year,
      value: assetsTotal - liabilityTotal
    };
  });

  const assetsTerminalTotal = assetsToShow
    .filter(a => !a.isInvalid && !a.toBeMerged)
    .reduce((sum, a) => sum + getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year), 0);
  const liabilityTerminalTotal = liabilitiesToShow
    .filter(l => !l.isInvalid && !l.toBeMerged)
    .reduce((sum, l) => sum + getValueAtMonthYear(l, terminalMonthYear?.month, terminalMonthYear?.year), 0);
  equityValues.push({
    month: terminalMonthYear?.month,
    year: terminalMonthYear?.year,
    value: assetsTerminalTotal - liabilityTerminalTotal
  });

  const annualAppreciation = getAnnualAppreciation(strategy);
  addRow(
    translate('equity') || 'Equity',
    equityValues,
    `${translate('annual_appreciation')} ${annualAppreciation?.toFixed(2)} %`
  );

  csvData.push(createEmptyRow());
  csvData.push(createEmptyRow());
  csvData.push(createEmptyRow());
  csvData.push(createEmptyRow());

  return { columns, datas: csvData };
};

const transformCashflowToCSV = (strategy, expandedMonthYears, terminalMonthYear, translate, hideRowsWithNoValue) => {
  // Create columns for react-csv-downloader
  const columns = [{ id: 'item', displayName: translate('cashflow') || 'Cashflow' }];
  expandedMonthYears.forEach(({ month, year }) => {
    columns.push({
      id: `${month}-${year}`,
      displayName: `${getTranslatedMonth(translate, month, { short: true })} ${year}`
    });
  });
  columns.push({ id: 'terminal', displayName: translate('terminal_value') || 'Terminal Value' });

  const csvData = [];

  // Helper function to create empty row
  const createEmptyRow = () => {
    const row = { item: '' };
    expandedMonthYears.forEach(({ month, year }) => {
      row[`${month}-${year}`] = '';
    });
    row.terminal = '';
    return row;
  };

  // Helper function to add a row
  const addRow = (name, values) => {
    const row = { item: name };
    expandedMonthYears.forEach(({ month, year }) => {
      const value = values.find(v => v.month === month && v.year === year);
      row[`${month}-${year}`] = value ? Math.round(value.value) : 0;
    });
    const terminalValue = values.find(v => v.month === terminalMonthYear?.month && v.year === terminalMonthYear?.year);
    row.terminal = terminalValue ? Math.round(terminalValue.value) : 0;
    csvData.push(row);
  };

  const cashflowsToShow = strategy.cashflows ? strategy.cashflows.filter(cf => !cf.toBeMerged) : [];

  // Income Section
  const incomeHeaderRow = { item: translate('income') || 'Income' };
  expandedMonthYears.forEach(({ month, year }) => {
    incomeHeaderRow[`${month}-${year}`] = '';
  });
  incomeHeaderRow.terminal = '';
  csvData.push(incomeHeaderRow);
  const incomeItems = sortInstruments(
    cashflowsToShow.filter(item => item.type === 'inflow' && (!hideRowsWithNoValue || hasMonthlyValues(item)))
  );

  incomeItems.forEach(item => {
    const values = expandedMonthYears.map(({ month, year }) => ({
      month,
      year,
      value: getValueAtMonthYear(item, month, year)
    }));
    values.push({
      month: terminalMonthYear?.month,
      year: terminalMonthYear?.year,
      value: getValueAtMonthYear(item, terminalMonthYear?.month, terminalMonthYear?.year)
    });
    addRow(item.name, values);
  });

  // Total Income
  const totalIncomeValues = expandedMonthYears.map(({ month, year }) => ({
    month,
    year,
    value: incomeItems
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce((acc, cashflow) => acc + getValueAtMonthYear(cashflow, month, year), 0)
  }));
  totalIncomeValues.push({
    month: terminalMonthYear?.month,
    year: terminalMonthYear?.year,
    value: incomeItems
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce(
        (acc, cashflow) => acc + getValueAtMonthYear(cashflow, terminalMonthYear?.month, terminalMonthYear?.year),
        0
      )
  });
  addRow(`${translate('total_income') || 'Total Income'}`, totalIncomeValues);
  csvData.push(createEmptyRow()); // Empty row for spacing

  // Expense Section
  const expenseHeaderRow = { item: translate('expenditure') || 'Expenditure' };
  expandedMonthYears.forEach(({ month, year }) => {
    expenseHeaderRow[`${month}-${year}`] = '';
  });
  expenseHeaderRow.terminal = '';
  csvData.push(expenseHeaderRow);
  const expenseItems = sortInstruments(
    cashflowsToShow.filter(item => item.type === 'outflow' && (!hideRowsWithNoValue || hasMonthlyValues(item)))
  );

  expenseItems.forEach(item => {
    const values = expandedMonthYears.map(({ month, year }) => ({
      month,
      year,
      value: getValueAtMonthYear(item, month, year)
    }));
    values.push({
      month: terminalMonthYear?.month,
      year: terminalMonthYear?.year,
      value: getValueAtMonthYear(item, terminalMonthYear?.month, terminalMonthYear?.year)
    });
    addRow(item.name, values);
  });

  // Total Expenses
  const totalExpenseValues = expandedMonthYears.map(({ month, year }) => ({
    month,
    year,
    value: expenseItems
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce((acc, cashflow) => acc + getValueAtMonthYear(cashflow, month, year), 0)
  }));
  totalExpenseValues.push({
    month: terminalMonthYear?.month,
    year: terminalMonthYear?.year,
    value: expenseItems
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce(
        (acc, cashflow) => acc + getValueAtMonthYear(cashflow, terminalMonthYear?.month, terminalMonthYear?.year),
        0
      )
  });
  addRow(`${translate('total_expenditure') || 'Total Expenditure'}`, totalExpenseValues);
  csvData.push(createEmptyRow()); // Empty row for spacing

  // Net Section
  const netValues = expandedMonthYears.map(({ month, year }) => {
    const income = incomeItems
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce((acc, cashflow) => acc + getValueAtMonthYear(cashflow, month, year), 0);
    const expense = expenseItems
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce((acc, cashflow) => acc + getValueAtMonthYear(cashflow, month, year), 0);
    return {
      month,
      year,
      value: income - expense
    };
  });

  const incomeTerminal = incomeItems
    .filter(a => !a.isInvalid && !a.toBeMerged)
    .reduce(
      (acc, cashflow) => acc + getValueAtMonthYear(cashflow, terminalMonthYear?.month, terminalMonthYear?.year),
      0
    );
  const expenseTerminal = expenseItems
    .filter(a => !a.isInvalid && !a.toBeMerged)
    .reduce(
      (acc, cashflow) => acc + getValueAtMonthYear(cashflow, terminalMonthYear?.month, terminalMonthYear?.year),
      0
    );
  netValues.push({
    month: terminalMonthYear?.month,
    year: terminalMonthYear?.year,
    value: incomeTerminal - expenseTerminal
  });
  addRow(translate('net') || 'Net', netValues);

  return { columns, datas: csvData };
};

//hack to hide popover
const hidePopover = () => document.body.click();

export const ActionCell = ({
  disabled,
  onEditClick,
  onDeleteClick,
  onAddEventClick,
  onDuplicateClick,
  onDisableClick,
  isItemNotApplied
}) => (
  <div style={{ display: 'flex', gap: '2px' }}>
    {onAddEventClick && (
      <ButtonTW
        disabled={disabled}
        variant="ghost"
        // size="icon"
        className="p-1 me-0"
        onClick={() => {
          onAddEventClick();
          hidePopover();
        }}
      >
        <PlusCircleIcon className="w-2 h-2 text-success" />
      </ButtonTW>
    )}
    {onDuplicateClick && (
      <ButtonTW
        disabled={disabled}
        variant="ghost"
        // size="icon"
        className="p-1 me-0"
        onClick={() => {
          onDuplicateClick();
          hidePopover();
        }}
      >
        <Copy className="w-2 h-2 text-info" />
      </ButtonTW>
    )}
    {onEditClick && (
      <ButtonTW
        disabled={disabled}
        variant="ghost"
        // size="icon"
        className="p-1 me-0"
        onClick={() => {
          onEditClick();
          hidePopover();
        }}
      >
        <PenIcon size={8} />
      </ButtonTW>
    )}

    {onDisableClick && (
      <ButtonTW
        disabled={disabled}
        variant="ghost"
        // size="icon"
        className="p-1 me-0"
        onClick={() => {
          onDisableClick();
          hidePopover();
        }}
      >
        {isItemNotApplied ? <X size={8} className="text-danger" /> : <Check size={8} className="text-success" />}
      </ButtonTW>
    )}
    {onDeleteClick && (
      <ButtonTW
        disabled={disabled}
        variant="ghost"
        // size="icon"
        className="p-1 me-0 "
        onClick={() => {
          onDeleteClick();
          hidePopover();
        }}
      >
        <Trash2 className="w-2 h-2 text-red-500" />
      </ButtonTW>
    )}
  </div>
);

const getStartAndEndMonthIndexOfAnYear = ({ year, initialMonth, initialYear, lastYear }) => {
  const startEndIndex =
    initialMonth === 0 || (year !== lastYear && year !== initialYear)
      ? { start: 0, end: 12 }
      : { start: year === lastYear ? 0 : initialMonth, end: year === lastYear ? initialMonth : 12 };

  return startEndIndex;
};

const TableHeaderRenderer = ({
  year,
  isExpanded,
  handleExpandYear,
  handleCollapseMonths,
  initialMonth = 0,
  initialYear,
  lastYear,
  currentMonth,
  currentYear,
  isCurrentYearExpanded,
  expandedYears,
  expandedMonthYears,
  ...props
}) => {
  const { translate } = useLocalization();
  const monthsToConsider = useMemo(() => {
    const startEndIndex = getStartAndEndMonthIndexOfAnYear({
      year,
      initialMonth,
      initialYear,
      lastYear
    });

    // For the current year
    if (currentYear === year) {
      if (isCurrentYearExpanded) {
        // Show all months if expanded
        return monthSmall.slice(startEndIndex.start, startEndIndex.end);
      } else {
        const startAndEndToShow = expandedMonthYears.filter(i => i.year === year);
        return startAndEndToShow.map(i => monthSmall[i.month]);
      }
    }

    // For other years
    if (expandedYears?.includes(year)) {
      return monthSmall.slice(startEndIndex.start, startEndIndex.end); // Expanded view
    }

    return monthSmall.slice(startEndIndex.start, startEndIndex.end);
  }, [year, initialMonth, initialYear, lastYear, currentYear, currentMonth, isCurrentYearExpanded, expandedYears]);

  if (currentYear === year && !isCurrentYearExpanded) {
    return monthsToConsider.map((monthName, index) => (
      <th
        className={`${monthName === monthSmall[currentMonth] ? 'masked-cell' : ''} bg-table-header text-white`}
        style={{ minWidth: 90 }}
        key={`${year}-${monthName}`}
        {...props}
        data-month={monthName}
      >
        {translate(monthName.toLowerCase()) || monthName} {year}
        <ArrowsAngleExpand
          size={10}
          onClick={() => handleExpandYear(year)}
          style={{ cursor: 'pointer', margin: '2px' }}
        />
      </th>
    ));
  }
  if (!isExpanded) {
    return (
      <th
        className={`${currentYear === year && currentMonth === 0 ? 'masked-cell' : ''} bg-table-header text-white`}
        style={{ minWidth: 90 }}
        key={`${year}-${monthSmall[0]}`}
        {...props}
        data-month={monthSmall[0]}
      >
        {translate(monthsToConsider[0]?.toLowerCase()) || monthsToConsider[0]} {year}
        <ArrowsAngleExpand
          size={10}
          onClick={() => handleExpandYear(year)}
          style={{ cursor: 'pointer', margin: '2px' }}
        />
      </th>
    );
  }

  return monthsToConsider.map((month, index) => (
    <th
      className={`${
        month === monthSmall[currentMonth] && currentYear === year ? 'masked-cell' : ''
      } negative-cell bg-table-header text-white`}
      style={{ minWidth: 90 }}
      key={year + month}
      {...props}
      data-month={month}
    >
      {translate(month.toLowerCase()) || month} {year}
      <ArrowsAngleContract
        size={10}
        onClick={() => handleCollapseMonths(year)}
        style={{ cursor: 'pointer', margin: '2px' }}
      />
    </th>
  ));
};

const CashFlowSection = forwardRef(
  (
    {
      disabled,
      expandedYears,
      expandedMonthYears,
      strategyYears,
      editable,
      strategy,
      handleExpandYear,
      handleExpandAll,
      handleCollapseAll,
      handleCollapseMonths,
      onEditClick,
      onDeleteClick,
      onAddCashflowClick,
      translate,
      onNameUpdateSubmit,
      isCurrentYearExpanded,
      setShowBottomScrollbar,
      handleScroll,
      isRTL,
      hideRowsWithNoValue = true,
      setHideRowsWithNoValue
    },
    ref
  ) => {
    const [expandedCashflows, setExpandedCashflows] = useState([]);

    const expandibleCashflows = useMemo(
      () =>
        !strategy.cashflows ? [] : [...new Set(strategy.cashflows.filter(i => i.toBeMerged).map(i => i.parentName))],
      [strategy]
    );

    const cashflowsToShow = useMemo(
      () =>
        !strategy.cashflows
          ? []
          : strategy.cashflows.filter(i => !i.toBeMerged || expandedCashflows.includes(i.parentName)),
      [strategy, expandedCashflows]
    );

    const onExpandCollapseClick = name => {
      if (expandedCashflows.includes(name)) {
        expandedCashflows.splice(expandedCashflows.indexOf(name), 1);
      } else {
        expandedCashflows.push(name);
      }
      setExpandedCashflows([...expandedCashflows]);
    };

    const incomeItems = useMemo(
      () =>
        sortInstruments(
          cashflowsToShow.filter(item => item.type === 'inflow' && (!hideRowsWithNoValue || hasMonthlyValues(item)))
        ),
      [cashflowsToShow, hideRowsWithNoValue]
    );

    const expenseItems = useMemo(
      () =>
        sortInstruments(
          cashflowsToShow.filter(item => item.type === 'outflow' && (!hideRowsWithNoValue || hasMonthlyValues(item)))
        ),
      [cashflowsToShow, hideRowsWithNoValue]
    );

    const tableRef = useRef(null);
    const stickyHeaderRef = useRef(null);
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const currentMonth = useMemo(() => new Date().getMonth(), []);
    const [alreadyScrolled, setAlreadyScrolled] = useState(false);
    const [tableHeadWidth, setTableHeadWidth] = useState(0);

    useEffect(() => {
      if (alreadyScrolled) return;
      if (!tableRef.current) return;

      setTimeout(() => {
        const toBeScrolledToCell = tableRef.current.querySelector(
          `[data-month="${monthSmall[currentMonth]}"][data-year="${currentYear}"]`
        );
        if (toBeScrolledToCell) {
          const parentContainer = tableRef.current.parentElement; // Get scrollable container
          if (parentContainer) {
            parentContainer.scrollLeft = toBeScrolledToCell.offsetLeft - 220;
          }
          setAlreadyScrolled(true);
        }
      }, 300); // Delay to ensure rendering is complete
    }, [expandedYears]);

    useEffect(() => {
      if (!ref?.current) return;
      stickyHeaderRef.current.scrollLeft = ref.current.scrollLeft;
    }, [ref?.current?.scrollLeft]);

    useEffect(() => {
      if (!tableRef?.current || !stickyHeaderRef?.current || !ref?.current) return;
      window.requestAnimationFrame(() => {
        const stickyHeaderRect = stickyHeaderRef?.current?.getBoundingClientRect();
        const headerHeight = stickyHeaderRect.height;
        const tableRect = ref?.current?.getBoundingClientRect();
        const actualTableRect = tableRef?.current?.getBoundingClientRect();
        const tableWidth = ref?.current?.offsetWidth;
        const stickyHeaderWidth = stickyHeaderRect.width;

        setTableHeadWidth(tableWidth);

        if (stickyHeaderRect.top <= 0 && tableRect.top <= headerHeight && actualTableRect.bottom - 80 > 0) {
          if (stickyHeaderWidth >= tableWidth) {
            stickyHeaderRef.current.style.position = 'fixed';
            stickyHeaderRef.current.style.top = 0;
            stickyHeaderRef.current.style.zIndex = 99999;
            stickyHeaderRef.current.style.overflowX = 'scroll';
            stickyHeaderRef.current.scrollLeft = ref.current.scrollLeft;
            setShowBottomScrollbar(true);
          }
        } else {
          const isFixed = stickyHeaderRef?.current?.style?.position !== 'static';
          if (isFixed) {
            window.scroll({
              top: window.scrollY + headerHeight,
              behavior: 'instant'
            });
            stickyHeaderRef.current.style.position = 'static';
          }
        }
      });
    }, [tableHeadWidth]);

    const internalScroll = e => {
      if (e.target === stickyHeaderRef.current && ref?.current) {
        ref.current.scrollLeft = e.target.scrollLeft;
        handleScroll(ref.current);
      } else {
        stickyHeaderRef.current.scrollLeft = e.target.scrollLeft;
        handleScroll(e);
      }
    };

    const terminalMonthYear = useMemo(() => {
      const { initialMonth, initialYear, totalYears = 10 } = strategy;
      const terminal = addMonthAndYear(initialMonth, initialYear, 0, totalYears);
      return terminal;
    }, [strategy]);

    useEffect(() => {
      if (!ref?.current) return;

      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          setTableHeadWidth(entry.contentRect.width);
        }
      });

      resizeObserver.observe(ref.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [ref?.current]);

    const terminalColumnStickyClass = useMemo(() => {
      return isRTL ? `sticky-column-left-100 thick-border-left` : `sticky-column-right-100 thick-border-right`;
    }, [isRTL]);

    return (
      <div>
        <div id="cashflowSection" className="d-flex align-items-center justify-content-between mb-2">
          <div className="flex-grow-1">
            <h6 className="mb-0">
              <b>{translate('cashflow')}</b>
            </h6>
          </div>

          <div className="d-flex gap-1 align-items-center">
            <ButtonTW variant="outline" size="sm" onClick={handleExpandAll}>
              <ArrowsAngleExpand className="w-3 h-3 me-1" />
              {translate('expand_all')}
            </ButtonTW>
            <ButtonTW variant="outline" size="sm" onClick={handleCollapseAll}>
              <ArrowsAngleContract className="w-3 h-3 me-1" />
              {translate('collapse_all')}
            </ButtonTW>
          </div>

          <div className="d-none d-md-block">
            <FormCheck
              size={'sm'}
              label={<b className="text-dark mid">{translate('hide_zero_rows')}</b>}
              checked={hideRowsWithNoValue}
              onChange={e => setHideRowsWithNoValue(e.target.checked)}
            />
          </div>
        </div>
        <div className="d-flex d-md-none mb-1">
          <FormCheck
            size={'sm'}
            label={<b className="text-dark mid">{translate('hide_zero_rows')}</b>}
            checked={hideRowsWithNoValue}
            onChange={e => setHideRowsWithNoValue(e.target.checked)}
          />
        </div>

        <div ref={ref} onScroll={internalScroll} className="table-responsive overflow-x-auto hide-scrollbar rounded-lg">
          <table ref={tableRef} responsive bordered className="smallFont m-0 table ">
            <thead
              style={{ overflowX: 'scroll', width: tableHeadWidth }}
              onScroll={internalScroll}
              ref={stickyHeaderRef}
              className="hide-scrollbar"
            >
              <tr className="bg-table-header text-white">
                <th style={{ minWidth: 140 }}></th>
                {strategyYears.map(year => (
                  <TableHeaderRenderer
                    key={year}
                    year={year}
                    lastYear={strategyYears[strategyYears.length - 1]}
                    initialMonth={strategy.initialMonth}
                    initialYear={strategy.initialYear}
                    isExpanded={expandedYears.includes(year)}
                    handleExpandYear={handleExpandYear}
                    handleCollapseMonths={handleCollapseMonths}
                    data-year={year}
                    currentYear={currentYear}
                    currentMonth={currentMonth}
                    expandedMonthYears={expandedMonthYears}
                    isCurrentYearExpanded={isCurrentYearExpanded}
                    expandedYears={expandedYears}
                  />
                ))}
                <th className={`bg-table-header text-white ${terminalColumnStickyClass}`} style={{ minWidth: 90 }}>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id="terminal-value-tooltip">{translate('terminal_value')}</Tooltip>}
                  >
                    <span>{truncate(translate('terminal_value'), { length: 7, omission: '...' })}</span>
                  </OverlayTrigger>
                </th>
                <th
                  className={`bg-table-header text-white ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}
                  style={{ minWidth: 100 }}
                >
                  {translate('actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'income', items: incomeItems, noDataText: 'no_incomes', totalText: 'total_income' },
                {
                  name: 'expenditure',
                  items: expenseItems,
                  noDataText: 'no_expenditure',
                  totalText: 'total_expenditure'
                }
              ].map(({ name, items, noDataText, totalText }) => (
                <React.Fragment key={name}>
                  <tr className="bg-white">
                    <td className="p-1" style={{ minWidth: 140 }}>
                      <b>{translate(name)}</b>
                    </td>
                    <td colSpan={expandedMonthYears.length} className="p-1 bg-white"></td>
                    <td className={`bg-white ${terminalColumnStickyClass}`}></td>
                    <td className={`bg-white ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}></td>
                  </tr>
                  {items.length > 0 ? (
                    items.map((item, index) => (
                      <tr
                        key={item._id || index}
                        className={
                          item.parentName && expandedCashflows.includes(item.parentName)
                            ? 'bg-secondary'
                            : `bg-${item.isInvalid ? 'danger-light' : 'white'}`
                        }
                      >
                        <td>
                          {expandibleCashflows.includes(item.name) && (
                            <ArrowsExpand
                              className="mx-1  hover-light"
                              onClick={() => onExpandCollapseClick(item.name)}
                            />
                          )}
                          <TextWithEdit
                            editable={editable}
                            text={item.name}
                            onSubmit={value => onNameUpdateSubmit('customCashflows', item, value)}
                          />
                        </td>
                        {expandedMonthYears.map(({ month, year }) => (
                          <BalanceCell
                            key={getKey(item._id, month, year)}
                            value={getValueAtMonthYear(item, month, year)}
                            disabled={disabled}
                            onEditClick={editable && onEditClick && (() => onEditClick(item))}
                            onDeleteClick={editable && item._id && onDeleteClick && (() => onDeleteClick(item))}
                            month={month}
                            year={year}
                            irr={item.irr}
                            irrCashflowBreakdown={item.irrCashflowBreakdown}
                          />
                        ))}
                        <BalanceCell
                          key={getKey(item._id, terminalMonthYear?.month, terminalMonthYear?.year)}
                          value={getValueAtMonthYear(item, terminalMonthYear?.month, terminalMonthYear?.year)}
                          month={terminalMonthYear?.month}
                          year={terminalMonthYear?.year}
                          className={`bg-white ${terminalColumnStickyClass}`}
                          onEditClick={editable && onEditClick && (() => onEditClick(item))}
                          onDeleteClick={editable && item._id && onDeleteClick && (() => onDeleteClick(item))}
                          bold
                        />

                        <td className={`bg-white ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}>
                          {!item.toBeMerged && (
                            <ActionCell
                              disabled={disabled || !editable}
                              onEditClick={!item.isAnonaPayment && (() => onEditClick(item))}
                              onDeleteClick={(item._id || item.isReinvestment) && (() => onDeleteClick(item))}
                            />
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td>{translate(noDataText)}</td>
                      <td colSpan={expandedMonthYears.length}></td>
                      <td className={`${terminalColumnStickyClass}`}></td>
                    </tr>
                  )}
                  <tr className="bg-gray-100">
                    <td className="p-1" style={{ minWidth: 110 }}>
                      <b>{translate(totalText)}</b>
                    </td>
                    {expandedMonthYears.map(({ month, year }) => (
                      <BalanceCell
                        key={`${year}-${month}`}
                        value={items
                          .filter(a => !a.isInvalid && !a.toBeMerged)
                          .reduce((acc, cashflow) => {
                            acc = acc + getValueAtMonthYear(cashflow, month, year);
                            return acc;
                          }, 0)}
                        month={month}
                        year={year}
                        className="bg-gray-100"
                      />
                    ))}

                    <BalanceCell
                      key={`${terminalMonthYear?.year}-${terminalMonthYear?.month}`}
                      value={items
                        .filter(a => !a.isInvalid && !a.toBeMerged)
                        .reduce((acc, cashflow) => {
                          acc = acc + getValueAtMonthYear(cashflow, terminalMonthYear?.month, terminalMonthYear?.year);
                          return acc;
                        }, 0)}
                      month={terminalMonthYear?.month}
                      year={terminalMonthYear?.year}
                      className={`bg-gray-100 ${terminalColumnStickyClass}`}
                      bold
                    />
                    <td
                      className={`bg-gray-100 ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}
                      style={{ minWidth: 100 }}
                    ></td>
                  </tr>
                </React.Fragment>
              ))}
              {/** NET */}
              <tr className="text-white bg-table-header">
                <td style={{ minWidth: 110 }}>
                  <strong className="text-white">{translate('net')}</strong>
                </td>
                {expandedMonthYears.map(({ month, year }) => (
                  <BalanceCell
                    key={`${year}-${month}`}
                    value={
                      incomeItems
                        .filter(a => !a.isInvalid && !a.toBeMerged)
                        .reduce((acc, cashflow) => acc + getValueAtMonthYear(cashflow, month, year), 0) -
                      expenseItems
                        .filter(a => !a.isInvalid && !a.toBeMerged)
                        .reduce((acc, cashflow) => acc + getValueAtMonthYear(cashflow, month, year), 0)
                    }
                    month={month}
                    year={year}
                    className="bg-table-header text-white"
                    bold
                  />
                ))}
                <BalanceCell
                  key={`${terminalMonthYear?.year}-${terminalMonthYear?.month}`}
                  value={
                    incomeItems
                      .filter(a => !a.isInvalid && !a.toBeMerged)
                      .reduce(
                        (acc, cashflow) =>
                          acc + getValueAtMonthYear(cashflow, terminalMonthYear?.month, terminalMonthYear?.year),
                        0
                      ) -
                    expenseItems
                      .filter(a => !a.isInvalid && !a.toBeMerged)
                      .reduce(
                        (acc, cashflow) =>
                          acc + getValueAtMonthYear(cashflow, terminalMonthYear?.month, terminalMonthYear?.year),
                        0
                      )
                  }
                  month={terminalMonthYear?.month}
                  year={terminalMonthYear?.year}
                  className={`bg-table-header text-white ${terminalColumnStickyClass}`}
                  bold
                />

                <td
                  className={`bg-table-header ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}
                  style={{ minWidth: 100 }}
                ></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

const typeToColor = {
  Add: 'success',
  Subtract: 'danger',
  Set: 'primary',
  Default: 'primary'
};

const typeToIcon = {
  Add: <ArrowUpRight size={8} className={`text-${typeToColor['Add']}`} />,
  Subtract: <ArrowDownRight size={8} className={`text-${typeToColor['Subtract']}`} />,
  Set: <ArrowReturnLeft size={8} className={`text-${typeToColor['Set']}`} />,
  Default: <CircleFill size={8} className={`text-${typeToColor['Default']}`} />
};

const TransactionRow = ({ transaction }) => {
  const { type, value, label } = transaction;
  return (
    <div className="d-flex tiny hover-light">
      <div>{typeToIcon[type || 'Default']}</div>
      <div className={`mx-1 flex-grow-1`}>
        <b className="text-muted">{label}</b>
      </div>
      <div className={`text-${typeToColor[type || 'Default']}`}>
        <b>
          {type === 'Subtract' ? '-' : ''}
          {formatCurrency(value)}
        </b>
      </div>
    </div>
  );
};

const AppreciatedBalance = ({ transaction, translate }) => {
  const appreciation = Number(transaction?.appreciation || 0);

  const showTotalInvestmentMadeSoFar =
    transaction?.totalInvestmentMadeSoFar !== undefined && transaction?.totalInvestmentMadeSoFar !== null;
  const showAppreciationValueSoFar =
    transaction?.appreciationValueSoFar !== undefined && transaction?.appreciationValueSoFar !== null;

  return (
    <div className="mb-1">
      <b>{translate('opening_balance')}: </b>
      {formatCurrency(transaction?.value)}
      <b
        className={`mx-1 tiny bg-${
          appreciation === 0 ? 'primary' : appreciation > 0 ? 'success' : 'danger'
        } px-1 rounded text-white`}
      >
        {appreciation.toFixed(2)}%
      </b>
      {(showTotalInvestmentMadeSoFar || showAppreciationValueSoFar) && (
        <>
          {showTotalInvestmentMadeSoFar && (
            <div className="tiny mt-1">
              <b className="text-muted">{translate('total_investment_made_so_far')}: </b>
              {formatCurrency(transaction?.totalInvestmentMadeSoFar)}
            </div>
          )}
          {showAppreciationValueSoFar && (
            <div className="tiny">
              <b className="text-muted">{translate('appreciation_value_so_far')}: </b>
              {formatCurrency(transaction?.appreciationValueSoFar)}
            </div>
          )}
          <hr className="my-1" />
        </>
      )}
    </div>
  );
};

const ValueCell = ({ bold, value }) =>
  bold ? (
    <b>
      <LTRNumberCell value={value} />
    </b>
  ) : (
    <LTRNumberCell value={value} />
  );

const mergeTransactions = (transactions = [], translate) => {
  //if same loan starts and ends in this month, do not show it in transactions
  let transactionsWithId = transactions.map((t, index) => ({ ...t, index }));

  //buyingTax, purchaseCost
  const allBuyAssetTransactions = transactionsWithId.filter(t => t.transactionType === 'assetBuy');
  //buyingTax, purchaseCost
  const allSellAssetTransactions = transactionsWithId.filter(t => t.transactionType === 'assetSell');

  //liability
  const allStartLoanTransactions = transactionsWithId.filter(
    t => t.transactionType === 'loanStart' && !t.isToClearInstallment
  );
  const allEndLoanTransactions = transactionsWithId.filter(t => t.transactionType === 'loanEnd');

  const allStartLoanForDebtTransactions = transactionsWithId.filter(
    t => t.transactionType === 'loanStart' && t.liability.isToClearInstallment
  );

  //liability
  const allInstallmentForDebtTransactions = transactionsWithId.filter(
    t => t.transactionType === 'payInstallment' && t.debtFor
  );

  const newTransactions = [];

  //combine buy and start loan into equity
  allBuyAssetTransactions.forEach(transaction => {
    //reduce costs from value
    transaction.value =
      transaction.value -
      (transaction.purchaseCost || 0) -
      (transaction.realtorCost || 0) -
      (transaction.renovationCost || 0) -
      (transaction.buyingTax || 0);

    //if it is real estate, combine loan and asset transaction to equity
    if (transaction.asset?.category === 'Real Estate') {
      const linkedTransactions = allStartLoanTransactions.filter(
        ({ liability }) =>
          !liability.isInvalid &&
          (liability.debtFor === transaction.asset.name ||
            (liability.relatedAssets && liability.relatedAssets.includes(transaction.asset.name)))
      );

      if (linkedTransactions.length) {
        let mergedTransaction = { ...transaction };
        const loanValue = linkedTransactions.reduce((acc, t) => acc + t.value, 0);
        mergedTransaction.value = mergedTransaction.value - loanValue;

        newTransactions.push(mergedTransaction);

        //remove all transactions that have been merged
        transactionsWithId = transactionsWithId.filter(
          ({ index }) => ![...linkedTransactions, transaction].map(t => t.index).includes(index)
        );
      }
    }

    // purchase cost transaction
    if (transaction.purchaseCost) {
      newTransactions.push({
        ...transaction,
        value: transaction.purchaseCost,
        label: `${translate('additional_purchase_cost_for')} ${transaction.asset.name}`
      });
    }
    // tax transaction
    if (transaction.buyingTax) {
      newTransactions.push({
        ...transaction,
        value: transaction.buyingTax,
        label: `${translate('buying_tax_for')} ${transaction.asset.name}`
      });
    }
    // realtor transaction
    if (transaction.realtorCost) {
      newTransactions.push({
        ...transaction,
        value: transaction.realtorCost,
        label: `${translate('realtor_cost_for')} ${transaction.asset.name}`
      });
    }
    // renovation cost
    if (transaction.renovationCost) {
      newTransactions.push({
        ...transaction,
        value: transaction.renovationCost,
        label: `${translate('renovation_cost_for')} ${transaction.asset.name}`
      });
    }
  });

  //combine sell and end loan into equity
  allSellAssetTransactions.forEach(transaction => {
    //reduce costs from value
    transaction.value = transaction.value + (transaction.salesCost || 0) + (transaction.sellingTax || 0);

    //if it is real estate, combine loan and asset transaction to equity
    if (transaction.asset?.category === 'Real Estate') {
      const linkedTransactions = allEndLoanTransactions.filter(
        ({ liability }) =>
          !liability.isInvalid &&
          (liability.debtFor === transaction.asset.name ||
            (liability.relatedAssets && liability.relatedAssets.includes(transaction.asset.name)))
      );

      if (linkedTransactions.length) {
        let mergedTransaction = { ...transaction };
        const loanValue = linkedTransactions.reduce((acc, t) => acc + t.value, 0);
        mergedTransaction.value = mergedTransaction.value - loanValue;

        newTransactions.push(mergedTransaction);

        //remove all transactions that have been merged
        transactionsWithId = transactionsWithId.filter(
          ({ index }) => ![...linkedTransactions, transaction].map(t => t.index).includes(index)
        );
      }
    }

    // sales cost transaction
    if (transaction.salesCost) {
      newTransactions.push({
        ...transaction,
        type: 'Subtract',
        value: transaction.salesCost,
        label: `Additional Sales Cost for ${transaction.asset.name}`
      });
    }
    // tax transaction
    if (transaction.sellingTax) {
      newTransactions.push({
        ...transaction,
        type: 'Subtract',
        value: transaction.sellingTax,
        label: `Selling Tax for ${transaction.asset.name}`
      });
    }
  });

  //combine pay debt and debt loan transaction
  allStartLoanForDebtTransactions.forEach(transaction => {
    const liability = transaction.liability;
    const linkedInstallmentTransactions = allInstallmentForDebtTransactions.filter(({ debtFor }) =>
      liability.relatedAssets.includes(debtFor)
    );

    if (linkedInstallmentTransactions.length) {
      let mergedTransaction = { ...transaction };
      const debtPaid = linkedInstallmentTransactions.reduce((acc, t) => acc + t.value, 0);

      const equityUsed = mergedTransaction.value - debtPaid;
      mergedTransaction.value = Math.abs(equityUsed);
      mergedTransaction.label = linkedInstallmentTransactions[0].label;
      mergedTransaction.index = linkedInstallmentTransactions[0].index;
      mergedTransaction.type = equityUsed > 0 ? 'Add' : 'Subtract';
      newTransactions.push(mergedTransaction);

      //remove all transactions that have been merged
      transactionsWithId = transactionsWithId.filter(
        ({ index }) => ![...linkedInstallmentTransactions, transaction].map(t => t.index).includes(index)
      );
    }
  });

  return [...transactionsWithId.filter(t => t.type !== 'opening_balance'), ...newTransactions].sort(
    (a, b) => a.index - b.index
  );
};

const BalanceCell = ({
  value,
  hideActionCell,
  bold = false,
  transactions,
  month,
  year,
  onEditClick,
  onDeleteClick,
  onAddEventClick,
  onDuplicateAssetLiabilityClick,
  disabled,
  irr,
  irrCashflowBreakdown,
  className
}) => {
  const { translate, langCode, isRTL } = useLocalization();
  const [show, setShow] = useState(false);
  const target = useRef(null);

  const mergedTransactions = useMemo(() => transactions && mergeTransactions(transactions, translate), [
    transactions,
    langCode
  ]);

  const hasIrr = useMemo(() => irr !== null && irr !== undefined, [irr]);
  const isCurrentMonthAndYear = useMemo(() => month === new Date().getMonth() && year === new Date().getFullYear(), [
    month,
    year
  ]);

  return (
    <>
      <td
        className={`${isCurrentMonthAndYear ? 'masked-cell' : ''} ${value < 0 ? 'negative-cell' : ''} ${className}`}
        style={{ minWidth: 90 }}
        ref={target}
        onClick={() => {
          if (window.getSelection().toString()) return;

          setShow(!show);
        }}
      >
        <span className="hover-light">
          <ValueCell value={Math.round(value)} bold={bold} />
        </span>
      </td>
      <Overlay target={target.current} show={show} placement="bottom" onHide={() => setShow(false)} rootClose>
        <Popover style={{ width: 300 }} id="popover-basic">
          <Popover.Header className={`px-1 ${mergedTransactions ? '' : 'bg-white rounded'}`}>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="mb-0 smallFont">
                  {mergedTransactions ? (
                    <>
                      {translate('transactions')}{' '}
                      <b>
                        ({getTranslatedMonth(translate, month, { short: true })} {year})
                      </b>
                    </>
                  ) : (
                    <b>
                      {getTranslatedMonth(translate, month, { short: true })} {year}
                    </b>
                  )}
                </h6>
              </div>
              {!hideActionCell && (
                <ActionCell
                  disabled={disabled}
                  onEditClick={onEditClick}
                  onDeleteClick={onDeleteClick}
                  onAddEventClick={onAddEventClick}
                  onDuplicateClick={onDuplicateAssetLiabilityClick}
                />
              )}
            </div>
          </Popover.Header>
          {(hasIrr || mergedTransactions) && (
            <Popover.Body className="p-1 smallFont">
              {mergedTransactions && (
                <>
                  <AppreciatedBalance
                    translate={translate}
                    transaction={transactions?.find(t => t.type === 'opening_balance')}
                  />
                  {mergedTransactions?.map((t, index) => (
                    <TransactionRow key={index} transaction={t} />
                  ))}
                </>
              )}
              {irr !== null && irr !== undefined && (
                <>
                  <hr className="mt-2 mb-1" />
                  <h6 className={`px-1 mb-0 smallFont text-${isRTL ? 'start' : 'end'}`}>
                    <b>{translate('irr')}: </b> <span>{formatPercentage(irr, false, true)} </span>{' '}
                    {irrCashflowBreakdown && <Summary data={irrCashflowBreakdown} extraInfo={{ timeLabel: 'month' }} />}
                  </h6>
                </>
              )}
            </Popover.Body>
          )}
        </Popover>
      </Overlay>
    </>
  );
};

const BalanceSheetSection = ({
  expandedMonthYears = [],
  disabled,
  onDuplicateAssetLiabilityClick,
  section,
  editable,
  strategy,
  onEditClick,
  onDeleteClick,
  onAddEventClick,
  translate,
  onNameSubmit,
  quickRenaming,
  isRTL,
  terminalMonthYear
}) => {
  const [expandedInstruments, setExpandedInstruments] = useState([]);
  const expandibleInstruments = useMemo(
    () => [...new Set(strategy[section.key].filter(i => i.toBeMerged).map(i => i.parentId))],
    [section, strategy]
  );

  const instrumentsToShow = useMemo(
    () => strategy[section.key].filter(i => !i.toBeMerged || expandedInstruments.includes(i.parentId)),
    [section, strategy, expandedInstruments]
  );

  const onExpandCollapseClick = a => {
    const id = a._id || a.id;
    if (expandedInstruments.includes(id)) {
      expandedInstruments.splice(expandedInstruments.indexOf(id), 1);
    } else {
      expandedInstruments.push(id);
    }
    setExpandedInstruments([...expandedInstruments]);
  };

  const terminalColumnStickyClass = useMemo(() => {
    return isRTL ? `sticky-column-left-180 thick-border-left` : `sticky-column-right-180 thick-border-right`;
  }, [editable, isRTL]);

  const appreciationColumnStickyClass = useMemo(() => {
    return isRTL ? `sticky-column-left-100` : `sticky-column-right-100`;
  }, [editable, isRTL]);

  return (
    <>
      <tr id={`${section.key}Section`} className=" text-white">
        <td className="p-1" style={{ minWidth: 140 }}>
          <b>{section.title}</b>
        </td>
        <td className="p-1" colSpan={expandedMonthYears.length}></td>
        <td className={`p-1 ${terminalColumnStickyClass}`} style={{ minWidth: 90 }}></td>

        <td style={{ minWidth: 80 }} className={`p-1 ${appreciationColumnStickyClass}`}>
          {/* <b className="text-white">{translate(section.key === 'assets' ? 'appreciation' : 'interest')}</b> */}
        </td>
        <td className={`p-1 ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}></td>
      </tr>
      {instrumentsToShow.length > 0 ? (
        section.key === 'assets' ? (
          assetCategoriesOptions.map(assetCategory => {
            const assetsOfThisCategory = instrumentsToShow.filter(a =>
              a.categoryToShowIn ? a.categoryToShowIn === assetCategory : a.category === assetCategory
            );

            return assetsOfThisCategory.length > 0 ? (
              <React.Fragment key={assetCategory}>
                {/**  Asset Category Name */}
                <tr>
                  <td className="flex items-center">
                    <span className="mx-2 font-semibold px-3 rounded-full bg-primary-light">
                      {translate(snakeCase(assetCategory)) || assetCategory}
                    </span>
                  </td>
                  <td colSpan={expandedMonthYears.length}></td>
                  <td className={` ${terminalColumnStickyClass}`}></td>
                  <td colSpan={2}></td>
                </tr>
                {/** All ITEMS OF THIS CATEGORY */}
                {sortInstruments(assetsOfThisCategory).map(a => (
                  <tr key={a._id} className={`bg-white`}>
                    <td>
                      {expandibleInstruments.includes(a._id || a.id) && (
                        <ArrowsExpand className="mx-2 hover-light" onClick={() => onExpandCollapseClick(a)} />
                      )}
                      <div className="mx-3">
                        <TextWithEdit
                          editable={editable && !isPrimaryBankAccount(a)}
                          text={isPrimaryBankAccount(a) ? translate('primary_bank_account') : a.name}
                          onSubmit={value => onNameSubmit(section.key, a, value)}
                          updateInProgress={quickRenaming}
                        />
                      </div>
                      {a.category === 'RSU' && (a.isVestedRSU ? '(Vested)' : '(Unvested)')}
                    </td>
                    {expandedMonthYears.map(({ month, year }) => (
                      <BalanceCell
                        disabled={disabled}
                        onDuplicateAssetLiabilityClick={
                          onDuplicateAssetLiabilityClick && (() => onDuplicateAssetLiabilityClick(a))
                        }
                        onEditClick={onEditClick && (() => onEditClick(a))}
                        onDeleteClick={onDeleteClick && !isPrimaryBankAccount(a) && (() => onDeleteClick(a))}
                        onAddEventClick={onAddEventClick && !isRSUAsset(a) && (() => onAddEventClick(a, month, year))}
                        key={getKey(a._id, month, year)}
                        hideActionCell={a.toBeMerged || (!a._id && !a.derivedFrom)}
                        value={getValueAtMonthYear(a, month, year)}
                        transactions={getTransactionsAtMonthYear(a, month, year)}
                        month={month}
                        year={year}
                        irr={a.irr}
                        irrCashflowBreakdown={a.irrCashflowBreakdown}
                      />
                    ))}
                    <BalanceCell
                      disabled={disabled}
                      onDuplicateAssetLiabilityClick={
                        onDuplicateAssetLiabilityClick && (() => onDuplicateAssetLiabilityClick(a))
                      }
                      onEditClick={onEditClick && (() => onEditClick(a))}
                      onDeleteClick={onDeleteClick && !isPrimaryBankAccount(a) && (() => onDeleteClick(a))}
                      key={getKey(a._id, terminalMonthYear?.month, terminalMonthYear?.year)}
                      hideActionCell={a.toBeMerged || (!a._id && !a.derivedFrom)}
                      value={getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year)}
                      month={terminalMonthYear?.month}
                      year={terminalMonthYear?.year}
                      irr={a.irr}
                      irrCashflowBreakdown={a.irrCashflowBreakdown}
                      className={`bg-white ${terminalColumnStickyClass}`}
                      bold
                    />
                    <td className={`bg-white ${appreciationColumnStickyClass}`} style={{ minWidth: 80 }}>
                      <LTRNumberCell value={a.returnAppreciation || a.interest || 0} formatType="percentage" />
                    </td>
                    <td
                      className={`bg-white ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}
                      style={{ minWidth: 100 }}
                    >
                      {!(a.toBeMerged || (!a._id && !a.derivedFrom)) && (
                        <ActionCell
                          disabled={disabled || !editable}
                          onEditClick={() => onEditClick(a)}
                          onDeleteClick={!isPrimaryBankAccount(a) && (() => onDeleteClick(a))}
                          onAddEventClick={!isRSUAsset(a) && (() => onAddEventClick(a))}
                          onDuplicateClick={() => onDuplicateAssetLiabilityClick(a)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
                {/** TOTAL OF THIS CATEGORY */}
                <tr className="bg-gray-100">
                  <td>
                    <b className="mx-2">{translate('total')}</b>
                  </td>
                  {expandedMonthYears.map(({ month, year }) => {
                    const value = assetsOfThisCategory
                      .filter(a => !a.isInvalid && !a.toBeMerged)
                      .reduce((prevValue, a) => prevValue + getValueAtMonthYear(a, month, year), 0);
                    return (
                      <BalanceCell
                        key={getKey(assetCategory, month, year)}
                        value={value}
                        bold
                        month={month}
                        year={year}
                        className="bg-gray-100"
                      />
                    );
                  })}

                  <BalanceCell
                    key={getKey(assetCategory, terminalMonthYear?.month, terminalMonthYear?.year)}
                    value={assetsOfThisCategory
                      .filter(a => !a.isInvalid && !a.toBeMerged)
                      .reduce(
                        (prevValue, a) =>
                          prevValue + getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year),
                        0
                      )}
                    bold
                    month={terminalMonthYear?.month}
                    year={terminalMonthYear?.year}
                    className={`bg-gray-100 ${terminalColumnStickyClass}`}
                  />
                  <td style={{ minWidth: 80 }} className={`${appreciationColumnStickyClass} bg-gray-100`}></td>
                  <td className={`${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'} bg-gray-100`}></td>
                </tr>
              </React.Fragment>
            ) : (
              <></>
            );
          })
        ) : (
          sortInstruments(instrumentsToShow).map(a => (
            <tr key={a._id} className={`bg-white`}>
              <td>
                {expandibleInstruments.includes(a._id || a.id) && (
                  <ArrowsExpand className="mx-1 hover-light" onClick={() => onExpandCollapseClick(a)} />
                )}
                <TextWithEdit
                  editable={editable}
                  text={a.name}
                  onSubmit={value => onNameSubmit(section.key, a, value)}
                  updateInProgress={quickRenaming}
                />
              </td>
              {expandedMonthYears.map(({ month, year }) => (
                <BalanceCell
                  disabled={disabled}
                  onDuplicateAssetLiabilityClick={
                    onDuplicateAssetLiabilityClick &&
                    !a.toBeMerged &&
                    a._id &&
                    (() => onDuplicateAssetLiabilityClick(a))
                  }
                  onEditClick={onEditClick && (() => onEditClick(a))}
                  onDeleteClick={onDeleteClick && a.name !== primaryBankAccountName && (() => onDeleteClick(a))}
                  onAddEventClick={onAddEventClick && (() => onAddEventClick(a, month, year))}
                  key={getKey(a._id, month, year)}
                  value={getValueAtMonthYear(a, month, year)}
                  transactions={getTransactionsAtMonthYear(a, month, year)}
                  month={month}
                  year={year}
                  hideActionCell={a.toBeMerged || (!a._id && !a.derivedFrom)}
                  irr={a.irr}
                  irrCashflowBreakdown={a.irrCashflowBreakdown}
                />
              ))}

              <BalanceCell
                key={getKey(a._id, terminalMonthYear?.month, terminalMonthYear?.year)}
                value={getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year)}
                month={terminalMonthYear?.month}
                year={terminalMonthYear?.year}
                hideActionCell={a.toBeMerged || (!a._id && !a.derivedFrom)}
                className={`bg-white ${terminalColumnStickyClass}`}
                bold
              />
              <td className={`bg-white ${appreciationColumnStickyClass}`}>{(a.interest || 0).toFixed(2)}%</td>
              <td className={`bg-white ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}>
                {!(a.toBeMerged || (!a._id && !a.derivedFrom)) && (
                  <ActionCell
                    disabled={disabled || !editable}
                    onEditClick={() => onEditClick(a)}
                    onDeleteClick={() => onDeleteClick(a)}
                    onAddEventClick={() => onAddEventClick(a)}
                    onDuplicateClick={() => onDuplicateAssetLiabilityClick(a)}
                  />
                )}
              </td>
            </tr>
          ))
        )
      ) : (
        <tr>
          <td>No {section.title}</td>
          <td colSpan={expandedMonthYears.length}></td>
          <td className={`${terminalColumnStickyClass}`}></td>
        </tr>
      )}
      {/* TOTAL OF THIS ASSETS/LIABILITIES */}
      <tr className="bg-gray-100">
        <td>
          <b>{translate(`total_${section.key}`)}</b>
        </td>
        {expandedMonthYears.map(({ month, year }) => {
          const value = instrumentsToShow
            .filter(a => !a.isInvalid && !a.toBeMerged)
            .reduce((prevValue, a) => prevValue + getValueAtMonthYear(a, month, year), 0);

          return (
            <BalanceCell
              key={getKey('total', month, year)}
              value={value}
              month={month}
              year={year}
              bold
              className="bg-gray-100"
            />
          );
        })}
        <BalanceCell
          key={getKey('total', terminalMonthYear?.month, terminalMonthYear?.year)}
          value={instrumentsToShow
            .filter(a => !a.isInvalid && !a.toBeMerged)
            .reduce(
              (prevValue, a) => prevValue + getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year),
              0
            )}
          month={terminalMonthYear?.month}
          year={terminalMonthYear?.year}
          bold
          className={`bg-gray-100 ${terminalColumnStickyClass}`}
        />
        <td style={{ minWidth: 80 }} className={`bg-gray-100 ${appreciationColumnStickyClass}`}></td>
        <td className={`bg-gray-100 ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}></td>
      </tr>
    </>
  );
};

const BalanceSheetView = forwardRef(
  (
    {
      strategy,
      strategyYears,
      disabled,
      editable,
      onDuplicateAssetLiabilityClick,
      onEditClick,
      onDeleteClick,
      expandedYears,
      expandedMonthYears,
      isCurrentYearExpanded,
      handleExpandYear,
      handleExpandAll,
      handleCollapseAll,
      handleCollapseMonths,
      onAddEventClick,
      translate,
      currentLanguage,
      onNameUpdateSubmit,
      quickRenaming,
      setBottomScrollbarWidth,
      setShowBottomScrollbar,
      setScrollableWidth,
      handleScroll,
      isRTL
    },
    ref
  ) => {
    const sections = useMemo(
      () => [
        { title: translate('assets'), key: 'assets' },
        { title: translate('liabilities'), key: 'liabilities' }
      ],
      [currentLanguage]
    );

    const tableRef = useRef(null);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const [alreadyScrolled, setAlreadyScrolled] = useState(false);
    const stickyHeaderRef = useRef(null);
    const [tableHeadWidth, setTableHeadWidth] = useState(0);

    useEffect(() => {
      if (alreadyScrolled) return;
      if (!tableRef?.current) return;

      const toBeScrolledToCell = tableRef?.current?.querySelector(
        `[data-month="${monthSmall[currentMonth]}"][data-year="${currentYear}"]`
      );

      if (toBeScrolledToCell) {
        setTimeout(() => {
          const parentContainer = tableRef?.current?.parentElement; // Get scrollable container
          if (parentContainer) {
            parentContainer.scrollLeft = toBeScrolledToCell.offsetLeft - 220;
          }
          setAlreadyScrolled(true);
        }, 100); // Delay to ensure rendering is complete
      }
    }, [expandedYears, tableRef?.current]);

    useEffect(() => {
      if (!tableRef?.current && !ref?.current) return;
      setScrollableWidth(tableRef?.current?.scrollWidth);
    }, [expandedMonthYears, expandedYears, tableRef?.current]);

    const internalScroll = e => {
      if (e.target === stickyHeaderRef.current && ref?.current) {
        ref.current.scrollLeft = e.target.scrollLeft;
        handleScroll(ref.current);
      } else {
        stickyHeaderRef.current.scrollLeft = e.target.scrollLeft;
        handleScroll(e);
      }
    };

    useEffect(() => {
      const handleScroll = () => {
        if (!tableRef?.current || !stickyHeaderRef?.current || !ref?.current) return;
        window.requestAnimationFrame(() => {
          const stickyHeaderRect = stickyHeaderRef?.current?.getBoundingClientRect();
          const headerHeight = stickyHeaderRect.height;
          const tableRect = ref?.current?.getBoundingClientRect();
          const actualTableRect = tableRef?.current?.getBoundingClientRect();
          const tableWidth = ref?.current?.offsetWidth;
          const stickyHeaderWidth = stickyHeaderRect.width;

          setTableHeadWidth(tableWidth);

          if (stickyHeaderRect.top <= 0 && tableRect.top <= headerHeight && actualTableRect.bottom - 80 > 0) {
            if (stickyHeaderWidth >= tableWidth) {
              stickyHeaderRef.current.style.position = 'fixed';
              stickyHeaderRef.current.style.top = 0;
              stickyHeaderRef.current.style.zIndex = 99999;
              stickyHeaderRef.current.style.overflowX = 'scroll';
              stickyHeaderRef.current.scrollLeft = ref.current.scrollLeft;
              setShowBottomScrollbar(true);
            }
          } else {
            const isFixed = stickyHeaderRef?.current?.style?.position !== 'static';
            if (isFixed) {
              window.scroll({
                top: window.scrollY + headerHeight,
                behavior: 'instant'
              });
              stickyHeaderRef.current.style.position = 'static';
            }
          }
        });
      };

      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, [tableHeadWidth]);

    useEffect(() => {
      if (!ref?.current) return;

      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          setTableHeadWidth(entry.contentRect.width);
          setBottomScrollbarWidth(entry.contentRect.width);
        }
      });

      resizeObserver.observe(ref.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [ref?.current]);

    useEffect(() => {
      if (!ref?.current) return;
      stickyHeaderRef.current.scrollLeft = ref.current.scrollLeft;
    }, [ref?.current?.scrollLeft]);

    const terminalMonthYear = useMemo(() => {
      const { initialMonth, initialYear, totalYears = 10 } = strategy;
      const terminal = addMonthAndYear(initialMonth, initialYear, 0, totalYears);
      return terminal;
    }, [strategy]);

    const terminalColumnStickyClass = useMemo(() => {
      return isRTL ? `sticky-column-left-180 thick-border-left` : `sticky-column-right-180 thick-border-right`;
    }, [isRTL]);

    const appreciationColumnStickyClass = useMemo(() => {
      return isRTL ? `sticky-column-left-100` : `sticky-column-right-100`;
    }, [isRTL]);

    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0">
            <b>{translate('balance_sheet')}</b>
          </h6>
          <div className="d-flex gap-1">
            <ButtonTW variant="outline" size="sm" onClick={handleExpandAll}>
              <ArrowsAngleExpand className="w-3 h-3 me-1" />
              {translate('expand_all')}
            </ButtonTW>
            <ButtonTW variant="outline" size="sm" onClick={handleCollapseAll}>
              <ArrowsAngleContract className="w-3 h-3 me-1" />
              {translate('collapse_all')}
            </ButtonTW>
          </div>
        </div>
        {strategy['assets'].length > 0 || strategy['liabilities'].length > 0 ? (
          <div
            ref={ref}
            onScroll={internalScroll}
            className="table-responsive overflow-x-auto hide-scrollbar rounded-lg"
          >
            <table ref={tableRef} responsive bordered className="smallFont table m-0 bg-white">
              <thead
                style={{ overflowX: 'scroll', width: tableHeadWidth }}
                onScroll={internalScroll}
                ref={stickyHeaderRef}
                className="hide-scrollbar"
              >
                <tr className="bg-table-header text-white">
                  <th style={{ minWidth: 140 }}></th>
                  {strategyYears.map(year => (
                    <TableHeaderRenderer
                      key={year}
                      lastYear={strategyYears[strategyYears.length - 1]}
                      initialMonth={strategy.initialMonth}
                      initialYear={strategy.initialYear}
                      year={year}
                      isExpanded={expandedYears.includes(year)}
                      expandedYears={expandedYears}
                      isCurrentYearExpanded={isCurrentYearExpanded}
                      handleExpandYear={handleExpandYear}
                      handleCollapseMonths={handleCollapseMonths}
                      data-year={year}
                      currentYear={currentYear}
                      currentMonth={currentMonth}
                      expandedMonthYears={expandedMonthYears}
                    />
                  ))}
                  <th className={`bg-table-header text-white ${terminalColumnStickyClass}`} style={{ minWidth: 90 }}>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="terminal-value-tooltip">{translate('terminal_value')}</Tooltip>}
                    >
                      <span>{truncate(translate('terminal_value'), { length: 7, omission: '...' })}</span>
                    </OverlayTrigger>
                  </th>
                  <th
                    className={`bg-table-header text-white ${appreciationColumnStickyClass}`}
                    style={{ minWidth: 80 }}
                  >
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="appreciation-tooltip">{translate('appreciation')}</Tooltip>}
                    >
                      <span>{truncate(translate('appreciation'), { length: 7, omission: '...' })}</span>
                    </OverlayTrigger>
                  </th>
                  <th
                    className={`bg-table-header text-white ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}
                    style={{ minWidth: 100 }}
                  >
                    {translate('actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sections.map(s => (
                  <BalanceSheetSection
                    key={s.key}
                    terminalMonthYear={terminalMonthYear}
                    editable={editable}
                    disabled={disabled}
                    onDuplicateAssetLiabilityClick={
                      editable &&
                      (item => {
                        const triggerInstrument = item.derivedFrom
                          ? strategy[s.key].find(i => i._id === item.derivedFrom)
                          : item;
                        onDuplicateAssetLiabilityClick(s.key, triggerInstrument);
                      })
                    }
                    onEditClick={
                      editable &&
                      (item => {
                        const triggerInstrument = item.derivedFrom
                          ? strategy[s.key].find(i => i._id === item.derivedFrom)
                          : item;
                        onEditClick(s.key, triggerInstrument);
                      })
                    }
                    onDeleteClick={
                      editable &&
                      (item => {
                        const triggerInstrument = item.derivedFrom
                          ? strategy[s.key].find(i => i._id === item.derivedFrom)
                          : item;
                        onDeleteClick(s.key, triggerInstrument);
                      })
                    }
                    onAddEventClick={
                      editable &&
                      ((item, month, year) => {
                        const triggerInstrument = item.derivedFrom
                          ? strategy[s.key].find(i => i._id === item.derivedFrom)
                          : item;
                        onAddEventClick(triggerInstrument, s.key === 'assets', month, year);
                      })
                    }
                    strategy={strategy}
                    section={s}
                    expandedMonthYears={expandedMonthYears}
                    onNameSubmit={onNameUpdateSubmit}
                    quickRenaming={quickRenaming}
                    translate={translate}
                    isRTL={isRTL}
                  />
                ))}
                {/** EQUITY */}
                <tr className="bg-table-header text-white">
                  <td style={{ minWidth: 110 }}>
                    <b className="text-white">{translate('equity')}</b>
                  </td>
                  {expandedMonthYears.map(({ month, year }) => {
                    const assetsTotal = strategy['assets']
                      .filter(a => !a.isInvalid && !a.toBeMerged)
                      .reduce((prevValue, a) => prevValue + getValueAtMonthYear(a, month, year), 0);

                    const liabilityTotal = strategy['liabilities']
                      .filter(a => !a.isInvalid && !a.toBeMerged)
                      .reduce((prevValue, a) => prevValue + getValueAtMonthYear(a, month, year), 0);

                    const value = assetsTotal - liabilityTotal;
                    // here
                    return (
                      <BalanceCell
                        key={getKey('total', month, year)}
                        value={value}
                        bold
                        month={month}
                        year={year}
                        className="bg-table-header text-white"
                      />
                    );
                  })}
                  <BalanceCell
                    key={getKey('total', terminalMonthYear?.month, terminalMonthYear?.year)}
                    value={
                      strategy['assets']
                        .filter(a => !a.isInvalid && !a.toBeMerged)
                        .reduce(
                          (prevValue, a) =>
                            prevValue + getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year),
                          0
                        ) -
                      strategy['liabilities']
                        .filter(a => !a.isInvalid && !a.toBeMerged)
                        .reduce(
                          (prevValue, a) =>
                            prevValue + getValueAtMonthYear(a, terminalMonthYear?.month, terminalMonthYear?.year),
                          0
                        )
                    }
                    month={terminalMonthYear?.month}
                    year={terminalMonthYear?.year}
                    bold
                    className={`bg-table-header text-white ${terminalColumnStickyClass}`}
                  />

                  <th
                    className={`bg-table-header text-white ${appreciationColumnStickyClass}`}
                    style={{ minWidth: 80 }}
                  >
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="annual-appreciation-tooltip">{translate('annual_appreciation')}</Tooltip>}
                    >
                      <span>{truncate(translate('annual_appreciation'), { length: 7, omission: '...' })}</span>
                    </OverlayTrigger>
                  </th>
                  <th
                    className={`bg-table-header text-white ${isRTL ? 'sticky-column-left-0' : 'sticky-column-right-0'}`}
                    style={{ minWidth: 100 }}
                  >
                    <LTRNumberCell value={getAnnualAppreciation(strategy)} formatType="percentage" />
                  </th>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <h6 className="p-5 text-center">{translate('nothing_to_show')}</h6>
        )}
      </div>
    );
  }
);

const StrategyBalanceSheet = ({
  strategy,
  onDuplicateAssetLiabilityClick,
  cashflowEditable,
  assetLiabilityEditable,
  disabled,
  onEditClick,
  onDeleteClick,
  onAddEventClick,
  onEditCashflowClick,
  onDeleteCashflowClick,
  onAddCashflowClick,
  onNameUpdateSubmit,
  quickRenaming,
  showCashflowTable,
  setBalanceSheetCashflowCsvMeta
}) => {
  const { translate, langCode, isRTL } = useLocalization();
  const bottomScrollbarRef = useRef(null);
  const balanceSheetRef = useRef(null);
  const cashflowRef = useRef(null);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [expandedYears, setExpandedYears] = useState([]);
  const [isCurrentYearFullyExpanded, setIsCurrentYearFullyExpanded] = useState(false);
  const [scrollableWidth, setScrollableWidth] = useState(0);
  const [bottomScrollbarWidth, setBottomScrollbarWidth] = useState(0);
  const [showBottomScrollbar, setShowBottomScrollbar] = useState(false);
  const [hideRowsWithNoCashflowValue, setHideRowsWithNoCashflowValue] = useState(true);

  // for name update on TextWithEdit
  const [confirmNameEditModalProps, setConfirmNameEditModalProps] = useState({
    show: false,
    item: null,
    section: null,
    newName: null
  });

  const handleNameUpdateSubmit = (section, item, newName) => {
    if (item.name === newName) {
      return onNameUpdateSubmit(section, item, newName);
    }

    setConfirmNameEditModalProps({
      show: true,
      section,
      item,
      newName
    });
  };

  const confirmNameUpdate = () => {
    const { section, item, newName } = confirmNameEditModalProps;
    onNameUpdateSubmit(section, item, newName);
    setConfirmNameEditModalProps({ show: false, item: null, section: null, newName: null });
  };

  const handleExpandYear = year => {
    if (year === currentYear) {
      setIsCurrentYearFullyExpanded(true); // Expand default view
    }
    setExpandedYears(prev => [...prev, year]);
  };

  const handleCollapseMonths = year => {
    if (year === currentYear) {
      setIsCurrentYearFullyExpanded(false); // Collapse to default view
    }
    setExpandedYears(prev => prev.filter(y => y !== year)); // Collapse other years
  };

  const strategyYears = useMemo(() => getStrategyYears(strategy), [strategy]);

  const handleExpandAll = () => {
    setExpandedYears(strategyYears);
    setIsCurrentYearFullyExpanded(true);
  };

  const handleCollapseAll = () => {
    setExpandedYears([]);
    setIsCurrentYearFullyExpanded(false);
  };

  const expandedMonthYears = useMemo(
    () =>
      strategyYears.flatMap(year => {
        const startEndIndex = getStartAndEndMonthIndexOfAnYear({
          year,
          initialMonth: strategy.initialMonth,
          initialYear: strategy.initialYear,
          lastYear: strategyYears[strategyYears.length - 1]
        });
        const months = monthSmall.slice(startEndIndex.start, startEndIndex.end);
        if (year === currentYear && !isCurrentYearFullyExpanded) {
          return Boolean(startEndIndex.start === currentMonth && currentMonth !== 0)
            ? [{ year, month: currentMonth }]
            : currentMonth !== 0
            ? [
                { year, month: startEndIndex.start },
                { year, month: currentMonth }
              ]
            : [{ year, month: startEndIndex.start }];
        }
        return (checkIfExpanded(expandedYears, year) ? months : [months[0]]).map((_, month) => ({
          year,
          month: month + startEndIndex.start
        }));
      }),
    [strategy, expandedYears, strategyYears, isCurrentYearFullyExpanded]
  );
  const handleScroll = event => {
    const targetDiv = event.target;
    if (targetDiv === bottomScrollbarRef.current && balanceSheetRef.current && cashflowRef.current) {
      balanceSheetRef.current.scrollLeft = targetDiv.scrollLeft;
      cashflowRef.current.scrollLeft = targetDiv.scrollLeft;
    } else if (targetDiv === balanceSheetRef.current && bottomScrollbarRef.current && cashflowRef.current) {
      bottomScrollbarRef.current.scrollLeft = targetDiv.scrollLeft;
      cashflowRef.current.scrollLeft = targetDiv.scrollLeft;
    } else if (targetDiv === cashflowRef.current && balanceSheetRef.current && bottomScrollbarRef.current) {
      balanceSheetRef.current.scrollLeft = targetDiv.scrollLeft;
      bottomScrollbarRef.current.scrollLeft = targetDiv.scrollLeft;
    }
  };

  useEffect(() => {
    if (!cashflowRef.current && !balanceSheetRef.current) return;
    const showBottomScrollbar = () => {
      const cashflowRefRect = cashflowRef.current?.getBoundingClientRect();
      const balanceSheetRefRect = balanceSheetRef.current?.getBoundingClientRect();

      if (!cashflowRefRect || !balanceSheetRefRect) {
        return;
      }

      if (balanceSheetRefRect.top < window.innerHeight && cashflowRefRect.bottom > 80) {
        setShowBottomScrollbar(true);
      } else {
        setTimeout(() => {
          setShowBottomScrollbar(false);
        }, 200);
      }
    };

    showBottomScrollbar();

    window.addEventListener('scroll', showBottomScrollbar);
    return () => window.removeEventListener('scroll', showBottomScrollbar);
  }, [cashflowRef, balanceSheetRef]);

  const terminalMonthYear = useMemo(() => {
    const { initialMonth, initialYear, totalYears = 10 } = strategy;
    const terminal = addMonthAndYear(initialMonth, initialYear, 0, totalYears);
    return terminal;
  }, [strategy]);

  useEffect(() => {
    if (setBalanceSheetCashflowCsvMeta) {
      setBalanceSheetCashflowCsvMeta({
        balanceSheetCsv: transformBalanceSheetToCSV(strategy, expandedMonthYears, terminalMonthYear, translate),
        cashflowCsv: transformCashflowToCSV(
          strategy,
          expandedMonthYears,
          terminalMonthYear,
          translate,
          hideRowsWithNoCashflowValue
        )
      });
    }
  }, [strategy, expandedMonthYears, terminalMonthYear, translate, hideRowsWithNoCashflowValue]);

  return (
    <React.Fragment>
      {!showCashflowTable ? (
        <BalanceSheetView
          strategy={strategy}
          strategyYears={strategyYears}
          editable={assetLiabilityEditable}
          disabled={disabled}
          onDuplicateAssetLiabilityClick={onDuplicateAssetLiabilityClick}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
          expandedYears={expandedYears}
          expandedMonthYears={expandedMonthYears}
          handleExpandYear={handleExpandYear}
          handleExpandAll={handleExpandAll}
          handleCollapseAll={handleCollapseAll}
          handleCollapseMonths={handleCollapseMonths}
          onAddEventClick={onAddEventClick}
          translate={translate}
          currentLanguage={langCode}
          onNameUpdateSubmit={handleNameUpdateSubmit}
          quickRenaming={quickRenaming}
          isCurrentYearExpanded={isCurrentYearFullyExpanded}
          ref={balanceSheetRef}
          setScrollableWidth={setScrollableWidth}
          setBottomScrollbarWidth={setBottomScrollbarWidth}
          setShowBottomScrollbar={setShowBottomScrollbar}
          handleScroll={handleScroll}
          isRTL={isRTL}
        />
      ) : (
        <CashFlowSection
          strategy={strategy}
          strategyYears={strategyYears}
          editable={cashflowEditable}
          disabled={disabled}
          expandedYears={expandedYears}
          expandedMonthYears={expandedMonthYears}
          handleExpandYear={handleExpandYear}
          handleExpandAll={handleExpandAll}
          handleCollapseAll={handleCollapseAll}
          handleCollapseMonths={handleCollapseMonths}
          onEditClick={onEditCashflowClick}
          onDeleteClick={onDeleteCashflowClick}
          onAddCashflowClick={onAddCashflowClick}
          translate={translate}
          onNameUpdateSubmit={onNameUpdateSubmit}
          isCurrentYearExpanded={isCurrentYearFullyExpanded}
          ref={cashflowRef}
          setScrollableWidth={setScrollableWidth}
          setShowBottomScrollbar={setShowBottomScrollbar}
          handleScroll={handleScroll}
          isRTL={isRTL}
          hideRowsWithNoValue={hideRowsWithNoCashflowValue}
          setHideRowsWithNoValue={setHideRowsWithNoCashflowValue}
        />
      )}

      {confirmNameEditModalProps.show && (
        <AlertModal
          show={confirmNameEditModalProps.show}
          onHide={() => setConfirmNameEditModalProps({ show: false, item: null, section: null, newName: null })}
          alertText={
            confirmNameEditModalProps.section === 'assets'
              ? translate('change_asset_name_confirm')
              : translate('change_liability_name_confirm')
          }
          continueButtonText={translate('yes')}
          onContinueClick={confirmNameUpdate}
        />
      )}
    </React.Fragment>
  );
};

export default StrategyBalanceSheet;
